import { getUltheraEvidenceReviews } from '../_data/evidence'

/** 본문 히어로와 홈 카드가 함께 사용하는 제목. */
export const ultheraEffectTitle = ["울쎄라 효과,", "바로 보인 사람도,", "기다린 사람도 있었어요"] as const

/**
 * 울쎄라 효과 페이지가 사용하는 검수 완료 정적 데이터.
 *
 * 원본: 울쎄라_100자이상_전체_260825.csv
 * 집계: 2026-08-25
 * 작성자명과 병원명은 소비자 화면에 전달하지 않는다.
 */
export const ultheraEffectCorpus = {
  validReviewCount: 13_478,
  excludedRowCount: 8,
  sourceCount: 5,
  sourceBadges: ["N", "G", "K", "M", "Y"],
  period: "2018.06.08~2026.08.25",
  countedAt: "2026.08.25",
} as const

export const ultheraEffectTableOfContents = [
  { id: "section-01", title: "받자마자 올라갈 거라 생각하면, 실망할 수 있어요" },
  { id: "section-02", title: "효과가 없나 싶었는데, 몇 주 뒤 턱선이 보였어요" },
  { id: "section-03", title: "3주쯤 지나자, 주변에서 살 빠졌냐고 물었대요" },
  { id: "section-04", title: "턱선도 눈가도, 변화를 적은 부위는 달랐어요" },
  { id: "section-05", title: "1년 반 뒤에도 턱선이 남았다는 사람이 있었어요" },
  { id: "section-06", title: "한 달 반을 기다렸는데도, 아무 변화가 없었어요" },
] as const

export const ultheraImmediateEffectComparison = {
  ariaLabel: "울쎄라 직후 체감이 달랐던 실제 후기 두 건",
  items: [
    {
      label: "받자마자 보였다는 사람",
      quote: "시술 직후부터 턱선이 착 끌어올려진 게 느껴졌고 한 달 지나면 더 예뻐진다니 너무 기대됩니다 ㅠㅠ 울쎄라 강추해요!",
      source: "네이버 지도 · 2026.08",
      href: "/ulthera/effect/evidence#evidence-NAVER_MAP_6a8688ea9957447755f4aba4",
      tone: "positive" as const,
    },
    {
      label: "직후에는 몰랐다는 사람",
      quote: "시술 직후에는 크게 모르겠더니 2~3주 정도 지나면서 턱라인이 조금씩 정리되는 게 느껴져서 만족스러워요.",
      source: "네이버 지도 · 2026.08",
      href: "/ulthera/effect/evidence#evidence-NAVER_MAP_6a7ea3bd9e2c111cdc4cba70",
      tone: "pending" as const,
    },
  ],
  note: "둘 다 한 사람이 직접 받은 뒤 시점을 적은 후기예요. 병행 시술을 적은 후기는 이 비교에서 제외했습니다.",
  takeaway: "받자마자 달랐다는 사람도, 몇 주 뒤에야 알았다는 사람도 있었어요. 오늘 거울만 보고 결론 내리지는 마세요.",
} as const

export const ultheraEffectSections = [
  {
    id: "section-01",
    number: "01",
    keyword: "울쎄라 효과 직후",
    titleLines: ["받자마자 올라갈 거라", "생각하면, 실망할 수 있어요"],
    body: "바로 턱선이 달라졌다는 사람도 있었지만, 직후에는 모르겠다고 적은 후기도 있었어요. 울쎄라는 당일 거울만 보고 결론 내리기에는 조금 이른 시술이었어요. 그래서 서로 다른 두 후기를 같은 무게로 놓고 봤습니다.",
    quote: "시술 직후에는 크게 모르겠더니 2~3주 정도 지나면서 턱라인이 조금씩 정리되는 게 느껴져서 만족스러워요.",
    quoteSource: "네이버 지도 · 2026.08",
    evidence: "받자마자 달랐다는 사람도, 몇 주 뒤에야 알았다는 사람도 있었어요. 오늘 거울만 보고 결론 내리지는 마세요.",
  },
  {
    id: "section-02",
    number: "02",
    keyword: "울쎄라 효과 언제부터",
    titleLines: ["효과가 없나 싶었는데,", "몇 주 뒤 턱선이 보였어요"],
    body: "직후에는 모르겠는데 2~3주가 지나 턱선이 정리됐다는 사람이 있었어요. 다른 후기는 시술 직후 눈꺼풀 변화를 보고, 한두 달 뒤 턱선을 알아봤다고 했고요. 처음 알아챈 시점을 하나로 정할 수는 없었습니다.",
    quote: "한두달 지나니까 턱선이 정리되는게 보입니다.",
    quoteSource: "구글 지도 · 2026.08",
    evidence: "직후와 몇 주 뒤의 이야기가 달랐어요. 내 경과는 언제 확인하면 좋을지 물어보세요.",
  },
  {
    id: "section-03",
    number: "03",
    keyword: "울쎄라 효과 주변 반응",
    titleLines: ["3주쯤 지나자,", "주변에서 살 빠졌냐고 물었대요"],
    body: "웨딩촬영을 앞두고 울쎄라를 받은 사람은 3주쯤 지나 주변에서 살 빠졌냐는 말을 들었다고 했어요. 또 다른 사람도 얼굴 라인이 정리된 걸 주변에서 알아봤다고 적었고요. 누가 먼저 알아챘는지까지 단정하지 않고, 실제로 들었다는 반응을 남겼습니다.",
    quote: "지금 3주 정도 지났는데 요즘 주변에서 살 빠졌냐는 말 은근 듣는 중...👀",
    quoteSource: "네이버 지도 · 2026.07",
    evidence: "본인의 체감뿐 아니라, 주변에서 어떤 말을 들었는지도 함께 읽어보세요.",
  },
  {
    id: "section-04",
    number: "04",
    keyword: "울쎄라 효과 부위",
    titleLines: ["턱선도 눈가도,", "변화를 적은 부위는 달랐어요"],
    body: "이중턱과 턱선, 볼살이 또렷해졌다고 적은 사람이 있었어요. 다른 사람은 직후 눈꺼풀에서 먼저 변화를 봤다고 했고요. 부위별 효과를 비교한 통계가 아니라, 각자가 변화를 느낀 곳을 적은 경험입니다.",
    quote: "이제 이중턱, 턱선, 볼살이 훨씬 또렷해지고 얼굴 전체적으로 갸름해진 느낌이에요.",
    quoteSource: "구글 지도 · 2026.08",
    evidence: "얼굴 전체라는 말보다, 내가 고민하는 부위를 적은 후기인지 확인해 보세요.",
  },
  {
    id: "section-05",
    number: "05",
    keyword: "울쎄라 효과 지속기간",
    titleLines: ["1년 반 뒤에도", "턱선이 남았다는 사람이 있었어요"],
    body: "두 번째 울쎄라를 받으러 온 사람은 첫 시술 후 1년 반이 지났는데도 턱선이 남아 있었다고 적었어요. 오래 만족했다는 개인 경험은 반갑지만, 모두에게 그만큼 지속되거나 같은 주기로 다시 받으면 된다는 뜻은 아닙니다.",
    quote: "첫 울쎄라 시술받은 지 1년 반 정도가 지났는데도 그때 꼼꼼히 시술해주신 덕분에 턱선이 남아있었어요ㅋㅋㅠㅠ",
    quoteSource: "네이버 지도 · 2025.10",
    evidence: "재시술 날짜와 효과가 사라진 날짜는 같지 않아요. 지금 내 상태를 기준으로 상담해 보세요.",
    variant: "start" as const,
  },
  {
    id: "section-06",
    number: "06",
    keyword: "울쎄라 효과 없음",
    titleLines: ["한 달 반을 기다렸는데도,", "아무 변화가 없었어요"],
    body: "한 달 반을 기다렸는데도 달라진 걸 전혀 모르겠다고 적은 사람도 있었어요. 시술 전 걱정이나 당일의 ‘아직 모르겠다’는 말은 효과 없음으로 세지 않았고요. 한 사람의 경험을 전체 결과처럼 말하지 않되, 이런 후기도 있었다는 건 그대로 남겼습니다.",
    quote: "시술 후 한달 반이 지났고 전혀전혀 효과가 1도 없습니다.",
    quoteSource: "네이버 지도 · 2026.01",
    evidence: "기다려도 효과를 못 느꼈다는 후기도 있었어요. 누구에게나 같은 결과가 오는 건 아니었어요.",
  },
] as const

export const ultheraEffectStory = {
  title: "어떤 사람의 세 주",
  beats: [
    { stage: "받은 직후", lines: ["얼굴이 퉁퉁 붓고", "약간의 욱신거림이 남았다."] },
    { stage: "3~4일", lines: ["만질 때마다 타박상을 입은 것처럼", "얼얼한 느낌이 이어졌다."] },
    { stage: "3주 뒤", lines: ["이중턱과 무너졌던 턱선이 붙으면서", "얼굴형이 정리된 게 보였다."] },
  ],
  quote: "결론부터 말씀드리면 효과는 확실하지만 과정은 만만치 않았어요.",
  quoteSource: "네이버 지도 · 2026.06",
  takeaway: "이 분은 울쎄라 600샷이었고, 세 주를 기다렸어요.",
} as const

export const ultheraEffectEvidenceReviews = getUltheraEvidenceReviews('effect')

export const ultheraEffectEvidenceCount = ultheraEffectEvidenceReviews.length
